// Suppresses Blake's demo WRITE handlers — the click handlers that mutate our
// real THREADS rows client-side, make NO server call, and toast as though the
// action succeeded.
//
// Asana 1217799695855666 (the class) and 1217805587637546 (the pay button).
// That card offers two options: (A) Blake rewires them to real endpoints, or
// (B) we intercept. This is B, and B is explicitly the INTERIM — it exists so
// the app claims nothing false while A is outstanding.
//
// 🔴 THE OLD LAST CLAUSE — "and it should be deleted the moment each handler
// becomes real" — IS WRONG, and following it would undo the work it describes.
// Option A never happened; what happened instead is that OUR layer wired the
// real write while HIS handler stayed in the bundle underneath it. Both are
// live on the same click. So the entry stays, and it stays for two reasons:
//
//   1. His handler claims the outcome SYNCHRONOUSLY, before any request can
//      resolve. A vote the server refuses has already been announced as
//      counted; an offer the server refuses has already been priced.
//   2. It is the fail-safe. If the real module throws or fails to install,
//      the control goes back to being DEAD rather than to lying.
//
// Measured, not argued: dealActionsLive.test.ts drops "[data-acceptoffer]" from
// the array and the invented fees land again on an offer the server 400'd.
// Real-and-still-guarded selectors include #wz-publish, [data-vote],
// [data-follow], [data-acceptoffer], [data-declineoffer], [data-withdraw],
// #want-save, [data-lfremove] and #dispute-file, among others wired since.
//
// 🔴 TWO ENTRIES HAVE NOW LEFT THIS ARRAY, AND NEITHER IS A PRECEDENT FOR
// THE OTHERS. "[data-owndelete]" left with its control (2026-09-28; see the
// note at its old place below): the ⋯ "Delete listing" itself is gone from the
// markup, so there is no handler to guard. "#promo-start" was removed when
// promoInstall.ts wired the real opt-in. Both reasons above are structurally
// answered there, which is what made it removable and does NOT make them
// removable:
//
//   1. Not synchronous. app.js's handler now checks window.__gbzPromoOptIn
//      FIRST and returns; the demo branch is unreachable once we install. The
//      others have no such fork — his handler and ours both run on one click.
//   2. The fail-safe is the render gate, not this array. #promo-start only
//      exists inside promoIsOffered(), which needs PROMO.source === "api", and
//      promoInstall sets the opt-in hook BEFORE it can flip that flag. A failed
//      install leaves the button UNRENDERED, which is stronger than dead. The
//      others sit on surfaces that paint either way, so dropping them restores the
//      lie instead of removing the control.
//
//      ⚠️ CORRECTED. This said "which only promoInstall sets", and that is
//      false: the demo bar's promo cycler at app.js:6814-6816 also sets
//      source:"api" — it is what makes the standalone mirror render at all.
//      The claim survives anyway, but for a DIFFERENT and weaker reason worth
//      stating plainly rather than overstating: overrides.css carries
//      `.blake-install-scope .demo-bar { display: none !important }` and
//      WebInstall.tsx:96 loads it, so on the live install that control is out
//      of hit-testing and cannot be clicked. The guarantee is a CSS rule in our
//      own layer, not an absence in his. Anyone who ever unhides the demo bar
//      on the install re-opens this door. Verified, not inherited — the comment
//      at app.js:6811 asserts this and was read against overrides.css:20.
//
// The test for this is promoInstall.test.ts ("a failed install leaves the
// promo surfaces unrendered"). Before removing any other entry, show the same
// two properties for it — measured, as dealActionsLive.test.ts measures the
// counter-case.
//
// 🔴 WHY THIS IS NOT COSMETIC. Before the lane was wired, these handlers
// decorated a fixture. They do not any more. #652 wired THREADS from
// /api/deal-threads and #655 wired `msgs` from the events endpoint, so
// `[data-completecheckout]` now pushes a FABRICATED message ("Paid and vaulted.
// Over to you.") into a LIVE conversation with a real counterparty who never
// received it, on top of painting "Payment Vaulted · Your money is in escrow ·
// @them was notified" — three clauses, all false, over a payment that never
// happened.
//
// Our own wiring is what made that worse, which is why the guard belongs in our
// layer rather than waiting on his.

/**
 * WHY CAPTURE PHASE ON THE DOCUMENT.
 *
 * Blake binds these with `el.addEventListener("click", ...)` — no capture flag —
 * so they run in the TARGET phase on the element itself. A listener registered
 * for the CAPTURE phase on `document` runs first, on the way down, before the
 * event ever reaches the element. Calling `stopPropagation()` there means his
 * handler is never invoked.
 *
 * 🪤 `stopPropagation()`, NOT `stopImmediatePropagation()`. Both stop the descent
 * to the target, which is all that is needed. The immediate variant would ALSO
 * kill every other capture listener on `document` — and we own two of those
 * (installDealThreadLoader's thread/summary listener, and installLinkFixups).
 * They do not currently overlap with anything guarded here, but choosing the
 * narrower call means a future overlap degrades to "the guard ran twice" rather
 * than "our own thread loader silently stopped working".
 */

/**
 * The handlers that make a claim we cannot honour.
 *
 * Each mutates real data, or asserts a server action that never happened, or
 * both — and none of them has a legitimate half worth preserving.
 */
export const GUARDED_WRITES: readonly string[] = [
  // 🔴🔴 The pay button. Fabricates a completed payment, marks PAY done in the
  // stepper, and writes a fake message into a real thread. app.js:4965.
  "[data-completecheckout]",
  /* 🔴 Sets `t.fees` with a hardcoded barterTax of 12 and shipping of 14, which
     is ALSO what makes #/checkout/<id> resolvable (app.js:3780). Accepting an
     offer that was never accepted, then pricing it with invented numbers.
     app.js:5182-5190.

     ⚠️ THIS ENTRY IS NOT STALE NOW THAT ACCEPT WORKS, and the reasoning is the
     same as [data-vote] and [data-follow] below rather than #wz-publish's.
     dealActions.ts now does the real PATCH /api/offers/:id/accept, installed
     after this guard on the same document, in capture — and because this
     handler calls stopPropagation() and NOT stopImmediatePropagation(), both
     run. Removing this line would not unblock anything; it would resurrect a
     fabricated acceptance that toasts SYNCHRONOUSLY, before the request
     resolves, racing the real refresh and re-arming the invented fees on a
     deal the server may still refuse ("Offer is no longer pending", "Add a
     shipping address..."). It is also the fail-safe: if dealActions ever
     fails to install, the button goes back to DEAD rather than to lying. */
  "[data-acceptoffer]",
  /* Mutates the thread and toasts that the counterparty was notified.
     app.js:5236-5241. Real as of dealActions.ts (PATCH /:id/decline) and
     STAYS, for the reason written out above. */
  "[data-declineoffer]",
  /* The cash-balance half of the counter sheet. Real as of dealCounter.ts
     (POST /api/offers with parentOfferId) and STAYS, same reasoning again —
     his handler rewrites both sides' cash, pushes a history row stamped with a
     hardcoded "Aug 22 · 12:05 PM", flips the row to WAITING and toasts that the
     counterparty has 48h, all synchronously and none of it true until the POST
     returns.
     🪤 One consequence to carry: his handler is also the only thing that clears
     `counterFor`, so with it suppressed the sheet does not close on its own.
     dealCounter closes it through the bridge instead. */
  "#counter-send",
  /* The Exchange's send. Real as of exchangeSend.ts (POST /api/offers with
     parentOfferId) and STAYS, same reasoning as the rest.

     🔴 STILL DEAD FOR A *FRESH* EXCHANGE OFFER, and deliberately so for now.
     exchangeSend's refusal 1 turns a missing `counterOf` into an out-loud
     refusal, which covers a genuinely new offer as well as the reload it was
     written for — the two are indistinguishable from `__gbzExchSides()`.
     Card 1218886330579015 wants the fresh half wired; it is NOT in the
     make-offer change, because sending on a null `counterOf` reintroduces the
     silent downgrade refusal 1 exists to stop. It needs the counter intent
     persisted so a reload can recover it, and that is its own change.

     🔴 THE PREVIOUS COMMENT HERE WAS WRONG IN BOTH HALVES, and it is worth
     recording because it nearly cost a server change. It said the web could
     not compose an exchange counter until the server carried side='requested'.
     The server already persists it (offers.ts inserts side:"requested" rows)
     AND already returns it: GET /api/offers/:id emits requestedItems plus
     requestedCashAmount, and its own comment says it exists "to re-prefill The
     Exchange from the counterer's perspective". Only the DEAL-THREADS routes
     filter to the offered side. The gap was one level up, in our transport,
     and exchangePrefill.ts closes it with a read we were always allowed.

     What his handler does, and why it may never run beside a real POST: it
     paints a 1.7s success screen, then rewrites BOTH sides of the thread from
     the deck and pushes a history row stamped with a hardcoded
     "Aug 21 - 11:30 AM". None of it is a server call. */
  "#ex-send",
  /* 🪤 THE OFFER WITHDRAW, NOT THE WALLET ONE — and the previous comment let
     that be read either way by listing this selector in a run of four with no
     distinction. `[data-withdraw]` is the "Withdraw Offer" button on a deal
     card (markup app.js:1902, handler app.js:4782); it retracts an OFFER. The
     money withdrawal is `#wal-withdraw-go` — "Withdraw $X" against a real
     available balance (markup app.js:2957, handler app.js:4478) — a different
     id, a different screen, and it was NOT covered by this entry for the whole
     time the grouped comment implied it was.

     🔑 Third instance of the name-plausibility trap in this lane: a selector
     whose NAME reads like it covers a neighbouring control, in a list where
     nothing forces the difference to be stated. `#dispute-count` (guarding a
     character counter) and the LISTINGS-vs-wallet reading of this one are the
     other two. The existing "<button> or <a>" test cannot catch this class —
     `[data-withdraw]` IS a real button, so it passes while guarding the wrong
     money path. The mechanical derivation in demoWriteGuardDerivation.test.ts
     is what closes it: it enumerates the handlers from Blake's source rather
     than trusting any name here to mean what it looks like.

     ⚠️ REAL AS OF dealActions.ts (PATCH /:id/cancel — 'withdrawn' is the
     wire's word for it) AND STAYS GUARDED, same reasoning as [data-acceptoffer].
     🪤 One consequence to know: his control is a two-stage arm and this guard
     stops it at stage ONE, so it never arms. dealActions owns the confirm step
     itself and cannot lean on his `dataset.armed` — the same shape already
     recorded for #wal-disconnect below. */
  "[data-withdraw]",
  // Sets `t.disputed`, pushes a DISPUTES row, paints a "GBZ-…" reference
  // number and "In review — both parties notified". app.js:4447-4454.
  //
  // 🔴 WAS `#dispute-count`, WHICH GUARDED NOTHING. That id is the character
  // counter inside the textarea's hint — `<span id="dispute-count">0</span> /
  // 2000` (app.js:3008), a text node with no click handler at all. The only
  // listener touching it is an `input` handler on the textarea that WRITES to
  // it (app.js:4446), which is the line the old comment cited. Clicking a
  // number is not how anyone files a dispute, so the fake-success path was
  // fully live: `#dispute-file` is the real submit control (app.js:3012,
  // `<button class="btn-offer dispute-file" id="dispute-file" disabled>File
  // Dispute</button>`) and app.js:4447's `dFile?.addEventListener("click", …)`
  // is the handler that mints the reference number. Both shapes checked — his
  // overlay markup and his wire() binding.
  //
  // Worst of the class after `#acct-pass`, and for the same reason: the
  // failure is invisible from inside the app. Someone hands over a reference
  // number for a dispute nobody received, and then waits.
  //
  // ⚠️ REAL AS OF disputeFile.ts (2026-09-28, card 1218889717010233): POST
  // /api/support/tickets with the deal's real offer id, the app's message shape,
  // and the server's GBZ-D- case reference on a 201. The entry STAYS as the
  // fail-safe, like the dealActions three: if that module fails to install, the
  // button goes back to dead rather than to minting a number.
  "#dispute-file",
  /* 🔴🔴 A MONEY PATH, and the largest gap the original sweep missed.
     "Offer sent to @X · they have 48h to respond · escrow…" (app.js:5109) over a
     button (app.js:811) that makes no server call. Same shape as the pay button:
     the visitor is told a counterparty has been given a deadline.

     ⚠️ ONE SELECTOR, TWO JOBS, AND BOTH ARE NOW REAL. His handler branches on
     `counterFor` (app.js): with a counter in progress it is the composer's Send
     Counter, which dealCounter.ts POSTs for real; with no counter it is the
     plain "Make offer" button on a listing, which makeOffer.ts POSTs for real
     as of card 1218886330579015. Before that it was wired on neither reading
     and this entry was the ONLY thing standing on the make-offer path.

     🔑 IT STAYS GUARDED, and the reason is now the ordinary one rather than a
     placeholder: his handler still fabricates. Removing this entry would ship
     "Offer sent to @X · they have 48h to respond · escrow reserved" beside the
     real POST — a sentence that is false twice over (the 48h is the buyer's
     PAY deadline anchored on accepted_at, dealDeadlines.ts:75; nothing reserves
     escrow at offer time). The guard is also what makes a failed install fall
     back to DEAD rather than to that sentence. */
  "[data-send-offer]",
  /* "Code re-sent — check your inbox" (app.js:4554). Same class as #acct-pass and
     the same reason it is severe: it fails OUTSIDE the app, so nothing on screen
     ever contradicts it and the person waiting is the one who cannot get in.
     🪤 This one is an <a>, not a <button> (app.js:2743) — which is what finally
     makes the guard's preventDefault() load-bearing rather than defensive. */
  "#verify-resend",
  /* "Thanks for your review · @X's rep updated" (app.js:4740). Claims a change to
     ANOTHER person's reputation. */
  "#review-close",
  /* "Saved · your changes are live" (app.js:3975) over a listing edit that
     persists nothing. */
  "#editl-save",
  /* 🔴 "Added · we'll flag matches the moment they appear" (app.js saveWantDraft) over a push
     into the page's myLookingFor and NO server call: the want vanished on reload and never
     reached the matcher (card 1218431365264102). Same blind spot as [data-follow] above,
     found the same way: myLookingFor is not one of the derivation's tracked roots.
     wantsWrite.ts does the real POST /api/wants and rebuilds the list from the server. */
  "#want-save",
  /* Its twin: the ✕ on a profile want spliced the same page-local list, so a removed want
     came back on reload. wantsWrite.ts does the DELETE for a want the server holds and the
     local splice for one the page alone holds (the tour's fixtures). */
  "[data-lfremove]",
  /* 🔴 "Profile updated" (app.js:4526) over a save that DISCARDS TWO OF THREE
     FIELDS. Reported by Blake 2026-08-31: "I tried to change my username and
     noticed it doesn't save."

     The handler reads #edit-name and #edit-handle only to VALIDATE them, then
     throws both away; only the bio is assigned, and only to a module-level
     variable that dies on reload. No fetch on any branch.

     ⚠️ THIS ENTRY IS NEW, AND ITS ABSENCE WAS THE POINT. Until now #edit-save
     was in NEITHER list — not guarded, not deliberately unguarded — so unlike
     every other fabricator here it lied with nothing suppressing it. It is
     invisible to demoWriteGuardDerivation's instrument because that derives on
     `writes && claims`, and writesSharedState() counts only THREADS / WALLET /
     USERS / DISPUTES / LISTINGS. A handler that CLAIMS success while writing
     nothing tracked can never derive. Worth remembering as a limit of that
     instrument: it finds fabricated writes, not pure lies.

     Guarded rather than left alone even though profileEdit.ts now does the real
     PATCH — for the [data-acceptoffer] reason above. The fabricator toasts
     SYNCHRONOUSLY, before any request resolves, so leaving it live would race a
     real save and announce success the server may still refuse with a 409 on a
     taken handle. It is also the fail-safe: if profileEdit fails to install,
     the button goes back to DEAD rather than to lying. */
  "#edit-save",
  // 🔑 "Password reset email sent" — sends nothing. The worst of the class,
  // because it fails OUTSIDE the app: the user goes to an inbox and waits, no
  // screen ever contradicts it, and the person who hits it is by definition
  // locked out. app.js:4998.
  "#acct-pass",
  // "Saved" — persists nothing, on a form pre-filled with someone else's real
  // address. app.js:4999.
  "#acct-save",

  /* ======================================================================
     THE WALLET (M2). Five handlers that move, hold or authorise MONEY on
     screen and make no server call whatsoever. Blake's bundle contains zero
     fetch / XMLHttpRequest / sendBeacon — measured, with a control, in
     demoWriteGuardDerivation.test.ts — so "makes no server call" is not a
     claim about these five, it is a property of the whole file.

     Everything here mutates the real WALLET object that #665's installWallet
     now fills from /api/wallet. The same upgrade that made the deals lane
     dangerous made this one dangerous: before the wallet was wired these
     buttons rearranged a fixture, and they do not any more.
     ====================================================================== */

  /* 🔴🔴 THE WORST OF THE FIVE. "✓ I received this — release funds" over a real
     escrow row, and the toast names the real counterparty: "Funds released to
     @<handle> · thanks for confirming" (markup app.js:2887, handler app.js:
     4497). It splices the row out of WALLET.escrowHeld and unshifts a
     "RELEASED" row into WALLET.escrowPast, so the money visibly leaves escrow
     and the seller is told they have been paid. Nobody was paid, and the
     person who clicked has been told they gave up their own escrow
     protection. */
  "[data-release]",
  /* 🔴 A withdrawal of a REAL available balance. Zeroes WALLET.availableCents,
     unshifts an "ON THE WAY" payout row, and toasts "On its way · Stripe
     typically lands it in 2 business days" (markup app.js:2957, handler
     app.js:4478). Same class as #acct-pass and worse for the same reason: it
     fails OUTSIDE the app. The screen says the money is moving, the balance
     reads zero to confirm it, and the wait is at a bank. */
  "#wal-withdraw-go",
  /* Pushes a hardcoded Mastercard ···· 4444 onto WALLET.cards and toasts "Card
     saved · Mastercard ···· 4444" (markup app.js:2972, handler app.js:4486).
     The invented last4 is what makes this a fabrication rather than a
     no-op: a payment method that does not exist is displayed as saved. */
  "#wal-savecard",
  /* Blake's handler claims a completed Stripe Connect onboarding after a
     1400ms wait ("<institution> ···· <last4> verified · payouts on",
     app.js:4671) — "verified" being an assertion about an identity check that
     never ran, the highest-consequence fabrication in this file.

     WIRED **AND** GUARDED (2026-08-30, card 1217981132152628; corrected same
     day by Ceci's review of #756). stripeConnectLaunch.ts runs the REAL
     create → onboarding-link → redirect chain on this selector, and this entry
     stays as the FAIL-SAFE beneath it — if the launcher ever fails to install
     (module load error, a boot-sequence exception, a WebInstall refactor), the
     guard still keeps the fabrication dead instead of letting it lie.

     🪤 THE TWO LISTENERS DO NOT RACE, which is why coexistence is safe and a
     removed entry buys nothing. Both are capture listeners on `document`, and
     this guard uses stopPropagation(), never stopImmediatePropagation() — its
     own header says why. stopPropagation stops the DESCENT to Blake's
     target-phase handler; it cannot stop a sibling listener on the same node
     in the same phase, in either registration order. The launcher consults
     only its selector and its in-flight flag, never defaultPrevented, so a
     guard that fired first cannot suppress it. Same resolution as
     #wz-publish (#708): where the real write is wired it runs alongside this;
     the entry stays either way. An earlier note here claimed first-registered
     wins and removed the entry on that basis — the mechanics were wrong, and
     demoWriteGuard.test.ts now pins the coexistence in the guard-first order
     the boot actually uses. */
  "#wal-connect",
  /* WALLET.bank.connected = false, "Stripe account
     disconnected · payouts paused" (markup app.js:2861, handler app.js:4505).
     🪤 Two-stage, arm-then-confirm — the first click only toasts "Withdrawals
     pause until you reconnect. Tap again to disconnect." and sets
     dataset.armed. The guard suppresses the click before either stage, so the
     control never arms; that is the intended outcome, not a half-guard. */
  "#wal-disconnect",
  /* 🔎 NOT ON THE CARD — found by the mechanical derivation, not by the sweep.
     Rewrites isDefault across every row of WALLET.cards and toasts "Default
     card updated" (handler app.js:4492). It sits in the middle of the same
     wallet wire() block as the four above and was simply missed. Guarded for
     the same reason as #wal-savecard: which card is default decides which one
     gets charged, and a client-side flip that says "updated" is a claim about
     the next payment. */
  "[data-setdefault]",

  /* ======================================================================
     THE COMPOSERS (F3). Both push a fabricated message into a thread that
     #655 now fills with REAL conversation history from the events endpoint.
     Neither toasts — and that is what made them easy to miss. The claim is
     not a toast, it is the SENT BUBBLE: the visitor's own words appear in a
     real thread, right-aligned, indistinguishable from a message that was
     delivered, to a counterparty who will never see it.
     ====================================================================== */

  /* The thread-view composer (markup app.js:1942, handler app.js:5065).
     Pushes {from:"me", text, when:"now"} onto t.msgs and re-renders.

     🔑 ALSO COVERS ITS ENTER KEY, and that is verified rather than assumed.
     `#msg-in`'s keydown handler is `if (e.key === "Enter") $("#msg-send")
     .click()` (app.js:5074) — a SYNTHETIC click, and a synthetic .click()
     dispatches a real event through the full capture path, so this
     click-phase guard sees it. Executed, not reasoned about: pressing Enter
     on #msg-in with a document capture listener installed reports the click
     with target `msg-send`. That is why #msg-in is deliberately absent from
     GUARDED_ENTER_INPUTS below. */
  /* The deal thread's composer. Real as of dealMessaging.ts (get-or-create a
     conversation, then POST /:id/messages) and STAYS.
     🪤 Two reasons here, not one. His handler pushes a bubble into a LIVE
     conversation with no server call, so a message the server refuses would
     already be on screen. AND it reads THREADS.find(x => x.id === dealsSel)
     with dealsSel null until a row is clicked, so on the first screen a
     visitor lands on it throws TypeError on t.msgs.push — this entry is the
     only reason that is not a live crash. Pinned in dealMessagingLive. */
  "#msg-send",
  /* The side-rail composer (markup app.js:3151, handler app.js:4251, which
     binds the shared `railSend` closure defined at app.js:4224).

     🔴 railSend ALSO PATCHES THE DOM DIRECTLY (app.js:4235-4238): it removes
     the ".msg-rail-empty" placeholder and insertAdjacentHTML's the bubble
     straight into #msgrail-msgs, deliberately bypassing the re-render ("patch
     the rail in place — no re-render, no flash"). So suppressing the write to
     t.msgs is not enough on its own to be sure nothing was claimed; the test
     for this entry asserts BOTH t.msgs and the rail's innerHTML are unchanged.

     🪤 AND ITS ENTER KEY IS NOT COVERED HERE. `#msgrail-in`'s keydown calls
     railSend() DIRECTLY (app.js:4252) — no click is ever dispatched, so no
     click-phase guard can see it. Measured: pressing Enter on #msgrail-in with
     a document capture listener installed reports ZERO clicks, while t.msgs
     gains a message and the bubble lands in the DOM. That path is closed by
     GUARDED_ENTER_INPUTS below, which is why this file guards two events. */
  /* The side rail's send. Real as of dealMessaging.ts and STAYS — his handler
     patches the bubble straight into the DOM without even a re-render, so it
     is the most convincing of the fabricators and the least recoverable.
     🔑 Its Enter key is covered by GUARDED_ENTER_INPUTS below, and that entry
     is derived from THIS one — removing this line silently unguards the key
     as well. */
  "#msgrail-send",

  /* ======================================================================
     FOUND BY THE DERIVATION, on no card. See
     demoWriteGuardDerivation.test.ts for how, and why an enumeration from
     Blake's source finds what a hand sweep does not.
     ====================================================================== */

  /* 🔴🔴 A MONEY CLAIM, and the most severe of the three. "Delivery confirmed ·
     escrow releases when both legs land" (handler app.js:4702) over a real
     thread: it flips the tracking leg to CONFIRMED, marks the DELIVER step
     done, and appends an event reading "You confirmed delivery · escrow
     releases when both legs land". Same family as [data-completecheckout] —
     the visitor is told a condition for releasing their money has been met and
     recorded. Nothing was recorded. */
  "[data-confirmleg]",
  /* 🔴 The dispute RESPONSE, and the exact twin of #dispute-file directly
     above (handler app.js:4687). Sets t.incomingDispute.responded, unshifts a
     DISPUTES row with status "Response sent", and sets respondDone so the
     overlay paints a confirmation. #dispute-file is carded as "worst of the
     class after #acct-pass" because the failure is invisible from inside the
     app; this is the same failure on the other side of the same dispute, and
     the person it strands is the one who has been accused. That #dispute-file
     was carded and this was not is the clearest evidence in this batch that a
     hand sweep enumerates screens, not handlers. */
  "#respond-send",

  /* ======================================================================
     THE LISTINGS WRITERS.

     🔴 LISTINGS IS REAL DATA, and leaving it out of the derivation's root set
     was this file's own inconsistency, not a judgement call: #editl-save was
     already guarded here — by hand, as a LISTINGS writer — while the
     instrument that checks this list did not count LISTINGS as shared state at
     all. A root set that contradicts the list it validates cannot certify
     anything, and the honest reconciliation is to widen the roots and guard
     the class, not to narrow the claim.

     installFloorListings REPLACES the array wholesale from GET /api/listings,
     and installLiveListingComments fills `comments` from
     GET /api/comments/:listingId. Every handler below therefore edits real
     rows belonging to real sellers.
     ====================================================================== */

  /* 🔴 "Listed. It's live on the floor." (app.js:4902) — LISTINGS.unshift of a
     brand-new row, plus a MY_COLLECTION row, then navigates to the listing it
     just invented. Zero POSTs. The single most consequential false claim in
     the file: it is the last button of the create flow, so the seller's whole
     session ends on a sentence that is not true, and they walk away believing
     their item is discoverable.

     ⚠️ GUARDING THIS DEADENS THE WIZARD'S FINAL BUTTON, and that is the
     accepted cost under this file's own philosophy — a control that does
     nothing is a bug report, a control that says "it's live on the floor" and
     does nothing is a seller waiting for offers that cannot come.

     🔑 THE LANE ARRIVED (2026-08-28) AND THIS ENTRY STAYS. wizardPublish.ts now
     POSTs the draft for real, and it is installed AFTER this guard on the same
     document, in capture. Because this handler calls stopPropagation() and NOT
     stopImmediatePropagation(), both run: the guard kills Blake's fabricator,
     the interceptor does the real work. Removing this entry would not "unblock"
     anything — it would resurrect the fabricator alongside the real POST and
     put an invented row on the floor beside the genuine one.

     That ordering is also the fail-safe. If wizardPublish ever fails to install
     or throws before its own work begins, the button goes back to being merely
     dead instead of lying. Deleting this line is the only way to lose that, so
     it is not deleted. */
  "#wz-publish",
  /* 🔎 ONE ENTRY LEFT HERE WITH ITS CONTROL: "[data-owndelete]", the listing
     page's ⋯ "Delete listing" ("Listing deleted" over a client-side
     LISTINGS.splice + MY_COLLECTION splice, thirteen lines from #editl-save in
     the same wire() block — how a hand sweep misses a neighbor). Blake folded
     the control into "Remove from your collection" at the bottom of Edit
     (2026-09-28, card 1218941367836566): the markup, the handler and this entry
     went together, because the "<button> or <a> in his markup" test rightly
     refuses an entry with nothing behind it. Not a precedent for the others
     either: nothing was wired, the control itself is gone, and its one job is
     done by [data-mgract]'s guarded "remove:" branch and collectionRemove.ts. */
  /* The inline "take" composer on a listing card (app.js:4095, through the
     shared `postTake` closure at app.js:4084). Pushes a comment onto a real
     row's `comments` array — the same array #656 fills from the comments
     endpoint — bumps commentCount, and toasts "Take posted".

     🔑 ALSO NEEDS ITS ENTER KEY, and it is the SECOND instance of the direct-
     call shape, not the unique one this file previously claimed. See
     GUARDED_ENTER_INPUTS. */
  "[data-takesend]",
  /* The comments-modal composer (app.js:4283). Pushes the same fabricated
     comment onto the same real row. No toast — the claim is the POSTED
     COMMENT, visible under a real listing with the visitor's own handle on it,
     exactly the reasoning that put #msg-send in this list.
     🪤 Its Enter key (#cm-in, app.js:4293) synthesises `$("#cm-send").click()`,
     so this entry covers it — the #msg-in shape, verified the same way. */
  "#cm-send",
  /* 🔴 The FAIR / OVER price vote (markup app.js:218 on the card and :918 on
     the detail page, handler app.js:4496). Writes store.votes[id] client-side
     and toasts "Vote counted · FAIR" over zero server calls.

     Worse than the usual shape: a SECOND click was refused with "You already
     called this one", so a mistap was permanent. The owner asked for click
     and unclick (2026-08-28) and listingVotes.ts now does the real POST and
     DELETE.

     ⚠️ THIS ENTRY IS NOT STALE NOW THAT VOTING WORKS, and the reasoning is
     the opposite of #wz-publish's. There, both handlers running is harmless.
     Here his toast fires SYNCHRONOUSLY, before the request resolves, so a
     vote the server refuses has already been announced as counted — and his
     "You already called this one" is exactly the refusal the change removes.
     The guard is what keeps either from reaching the screen, and it is still
     the fail-safe if the interceptor fails to install.

     📌 Guarding this cannot make a vote tap open the listing: the card's own
     handler already excludes [data-vote] by target check (app.js:4246), so
     his stopPropagation was never load-bearing for that. */
  "[data-vote]",
  /* The take heart (card preview, detail row, comments modal — app.js
     takeHeartHTML). His handler flips the class, a session Set and `c.likes`
     on a real seller's LISTINGS row with no server call: a like that vanishes on
     reload. commentLikes.ts now votes for real on the same element, keyed by the
     comment id it also carries, so letting his run beside it would count one tap
     twice (2026-09-10). */
  "[data-takelike]",
  /* 🔴 The Follow button (markup app.js:1408 on a profile, :2120 on a deal
     card, :3188 in the followers list; handler app.js:5199). It sets
     el.textContent = "Following ✓" and toasts "Following @X" over no server
     call, and the label reverts on the next repaint — so the claim is false
     twice: nothing happened, and the evidence removes itself.

     🪤 THIS ENTRY WAS ADDED BY HAND, and that is the point worth keeping.
     The derivation could never have found it: it enumerates handlers that
     write to SHARED STATE, and this one touches only el.textContent. So the
     instrument has a blind spot for DOM-ONLY handlers that make a false
     claim — no array written, nothing to enumerate, and a toast that lies.
     This is the first one found. Assume it is not the last, and do not read
     a clean derivation as proof there are none.

     followUser.ts does the real POST/DELETE. The guard suppresses his label
     and his toast, which would otherwise announce the follow synchronously,
     before the request resolves — same reasoning as [data-vote] above. */
  "[data-follow]",
  /* 🔴 The save tag (markup app.js:311 on a feed card, :1082 on the listing
     detail; handler app.js:7603-7608). Same blind spot as [data-follow]
     directly above, and found the same way, by hand: the handler flips a
     local SAVED Set, toggles .on and toasts "Saved · find it on your profile
     under My Saved Listings" / "Removed from your saved listings" — SAVED is
     not one of demoWriteGuardDerivation.test.ts's tracked roots (THREADS,
     WALLET, USERS, DISPUTES, LISTINGS), so the mechanical derivation cannot
     see this one either.

     ⚠️ THIS ENTRY MOVED HERE FROM DELIBERATELY_UNGUARDED, and it is not a
     new finding — it is the same control, now real. Until savedListings.ts
     existed, SAVED was purely local and self-correcting on reload, which is
     exactly what made the old entry's trade-off (leave a guest-reachable
     control alone rather than deaden it) the right call for a fixture that
     never called a server. Now that GET/POST /api/favorites back it for
     real, his handler's claim stops being harmless: it toasts and flips the
     class SYNCHRONOUSLY, before any request resolves, over a Set that would
     otherwise never be told to the server at all — a save the visitor is
     shown that never reaches their profile on another device or a reload.
     Same reasoning as [data-vote]/[data-follow], and the same fail-safe: if
     savedListings.ts ever fails to install, the tag goes back to being
     merely dead rather than lying again. */
  "[data-savelisting]",
];

/**
 * DELIBERATELY NOT GUARDED, and this list is the load-bearing half.
 *
 * `[data-msg]` · `[data-msgitem]` · `[data-gothread]` · `[data-selthread]`
 *
 * All four set `t.unread = 0` as a SIDE EFFECT of navigation. The navigation is
 * legitimate and must keep working — `[data-selthread]` is how a visitor opens a
 * thread at all, and it is the selector our OWN thread-detail loader listens on
 * (floorTransport.ts, installDealThreadLoader). Guarding them would break the
 * deals surface and our own wiring with it.
 *
 * What is lost by leaving them, since 2026-10-07, while dealsReadState.ts is
 * installed: little. `[data-selthread]` and `[data-gothread]` open a Deals thread,
 * `[data-msg]` and `[data-msgitem]` open the message rail. That seam hears the
 * same clicks first (capture phase), so the one row these handlers clear never
 * hides a dot from it, and once the thread or the rail is on screen it clears the
 * dot on every deal row of that person and tells the server. What remains: the
 * server can be told only once the person's conversation id is known, which comes
 * with any read of their thread's events (opening it on the Deals page, the
 * open-thread poll, a message sent from the rail or the thread, an offer
 * action's re-read), and not while the list knows a newer line from them than
 * the thread shows (the seam's hold), so a rail opened
 * before that id, or over a line it does not show, clears the dot client-side
 * only, and the next list read (a reload, or any action's re-read) brings it
 * back. That is a cosmetic, self-correcting inconsistency, and a far smaller
 * wrong than a navigation that does nothing.
 */
export const DELIBERATELY_UNGUARDED: readonly string[] = [
  "[data-msg]",
  "[data-msgitem]",
  "[data-gothread]",
  "[data-selthread]",
];

/**
 * ONE BRANCH of a control whose other branches are real.
 *
 * `[data-mgract]` is a single delegated handler on the collection card's
 * action buttons (app.js, mgrCard/mgrActions, and since card 1218941367836566
 * the phone's actions pop-up and the "Remove from your collection" button at
 * the bottom of both edit screens), and the attribute's VALUE picks the
 * action: "edit:" and "listit:" are wizardEdit.ts's door and must reach
 * Blake's handler, "view:", "order:", "sale:" and "trade:" navigate, "offers:"
 * opens a modal, and only "remove:" fabricates: it splices MY_COLLECTION and
 * toasts "Removed from your collection" over zero calls (card
 * 1218907631345203). It was in NEITHER list above, lying with nothing in
 * front of it.
 *
 * Guarding the whole selector would shut the Edit door, so a branch is matched
 * by the attribute AND its value's prefix. It is its own list rather than an
 * `[data-mgract^="remove:"]` entry in GUARDED_WRITES because every check on that
 * list reads a selector as exactly `[data-x]` or `#id` (demoContract's needle,
 * the <button>-or-<a> rule, the per-selector click test, the derivation's
 * enumerator), and a third shape would have been a silent hole in each.
 * collectionRemove.ts does the real DELETE alongside, the way wizardPublish
 * does for #wz-publish, and the branch stays here as the fail-safe.
 * demoWriteGuardDerivation.test.ts proves the handler's other branches write
 * nothing, which is what makes guarding one branch enough.
 */
export interface GuardedBranch {
  /** The handler's own selector, as the derivation enumerates it. */
  selector: string;
  attr: string;
  /** The attribute value's prefix that names the fabricating branch. */
  prefix: string;
}
export const GUARDED_BRANCHES: readonly GuardedBranch[] = [
  { selector: "[data-mgract]", attr: "data-mgract", prefix: "remove:" },
];

/** A branch as a CSS selector, for logs and onBlocked. */
export const branchSelector = (b: GuardedBranch): string => `[${b.attr}^="${b.prefix}"]`;

/**
 * THE KEYBOARD PATH — composer inputs whose Enter key reaches a guarded write
 * WITHOUT dispatching a click.
 *
 * WHY THIS EXISTS AT ALL. A capture-phase click guard is complete only for
 * controls that are actually clicked. Blake wires both message composers to
 * Enter as well, and he does it two DIFFERENT ways:
 *
 *   #msg-in     keydown -> $("#msg-send").click()   (app.js:5074)
 *   #msgrail-in keydown -> railSend()               (app.js:4252)
 *
 * The first synthesises a click, which traverses document capture like any
 * other and is caught by the click guard. The second calls the write function
 * directly — there is no click, at any phase, ever. Both were VERIFIED BY
 * EXECUTION before this code was written, because the difference is invisible
 * from reading the two lines side by side and getting it wrong in either
 * direction is expensive: guard both and one is dead code, guard neither and
 * the rail composer stays fully live behind a guard that looks complete.
 *
 * Measured, with a document-level capture listener recording every click:
 *   Enter on #msg-in      -> clicks seen: ["msg-send"], t.msgs gained a message
 *   Enter on #msgrail-in  -> clicks seen: [],           t.msgs gained a message
 *                                                       AND the bubble was
 *                                                       patched into the DOM
 *
 * `#msg-in` is therefore deliberately absent. Its coverage is not an accident
 * of this map — it is a consequence of `#msg-send` being in GUARDED_WRITES,
 * and if `#msg-send` were ever removed from there, the Enter path would
 * correctly unguard with it.
 *
 * 🪤 THE DIRECT-CALL SHAPE IS NOT UNIQUE, and an earlier version of this
 * comment said it was. `[data-takefor]`'s keydown calls `postTake()` directly
 * (app.js:4100) exactly as `#msgrail-in` calls `railSend()`, and it was missed
 * because the derivation's root set did not yet count LISTINGS as shared
 * state. Two of the four composer inputs take each shape:
 *
 *   direct call  (needs an entry here):  #msgrail-in -> railSend()
 *                                        [data-takefor] -> postTake()
 *   synthetic .click() (needs none):     #msg-in -> $("#msg-send").click()
 *                                        #cm-in  -> $("#cm-send").click()
 *
 * Calling a shape unique after finding one instance is what let the second one
 * ship unguarded; the pairs above are enumerated, not sampled.
 *
 * 🔑 NARROW BY CONSTRUCTION. Enter only, and only on inputs named here. A
 * document-wide "suppress Enter" would break every other form in the app
 * (search submit, the auth flow, the dispute textarea) to stop one composer.
 *
 * 🔑 NOT A SECOND HAND-LIST. The values are SEND-CONTROL selectors, and the
 * guard below only fires for an entry whose send control is currently in
 * GUARDED_WRITES. The keyboard path can therefore never guard more than the
 * click path does, and cannot drift from it in the dangerous direction — a
 * send control removed from GUARDED_WRITES, for whatever reason, does not keep
 * a suppressed Enter key. ✏️ That clause read "because it became real" until
 * 2026-09-28, which is the premise the header 600 lines above exists to
 * refute: becoming real is NOT why an entry leaves, and the header names the
 * selectors that are real and still guarded. The property is true either way; only the reason attached
 * to it was wrong. A test pins that every value here is in
 * GUARDED_WRITES, so an entry that stops corresponding to a real guard is a
 * red rather than a silent no-op.
 */
export const GUARDED_ENTER_INPUTS: Readonly<Record<string, string>> = {
  "#msgrail-in": "#msgrail-send",
  "[data-takefor]": "[data-takesend]",
};

/**
 * Install the guard. Returns a teardown.
 *
 * `onBlocked` is optional and intentionally unused by default. A visible "not
 * available yet" message is a PRODUCT decision — what it says, and whether a
 * disabled-looking control is better than a silent one — and inventing that copy
 * here would be the same class of mistake as the toasts being suppressed. The
 * hook exists so that decision can be attached later without touching the
 * mechanism.
 *
 * Until then the guard is SILENT to the user. A control that does nothing is a
 * bug report; a control that says "Payment Vaulted" and does nothing is a
 * support incident. Saying nothing is the worse product and the better outcome.
 */
export function installWriteGuard(
  win: Window,
  onBlocked?: (selector: string, el: Element) => void,
): () => void {
  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    for (const selector of GUARDED_WRITES) {
      const el = target.closest(selector);
      if (!el) continue;
      event.stopPropagation();
      event.preventDefault();
      console.warn(
        `[web-install] write-guard: suppressed "${selector}" — Blake's handler for this control ` +
          `makes no server call (Asana 1217799695855666). Where the real write is wired it runs ` +
          `alongside this; the entry stays either way — see the header.`,
      );
      onBlocked?.(selector, el);
      return;
    }
    for (const branch of GUARDED_BRANCHES) {
      const el = target.closest(branch.selector);
      if (!el || !(el.getAttribute(branch.attr) ?? "").startsWith(branch.prefix)) continue;
      event.stopPropagation();
      event.preventDefault();
      console.warn(
        `[web-install] write-guard: suppressed "${branchSelector(branch)}" — Blake's handler for this ` +
          `branch makes no server call. Where the real write is wired it runs alongside this.`,
      );
      onBlocked?.(branchSelector(branch), el);
      return;
    }
  };

  /* Enter, on a named composer input only — see GUARDED_ENTER_INPUTS.
     `onBlocked` is called with the SEND selector rather than the input's,
     because what was suppressed is the write, not the keystroke; the element
     passed is the input, which is where the visitor's attention is if that
     hook ever grows a visible message. */
  const onKeydown = (event: Event): void => {
    if ((event as KeyboardEvent).key !== "Enter") return;
    const target = event.target as Element | null;
    if (!target?.closest) return;
    for (const [inputSelector, sendSelector] of Object.entries(GUARDED_ENTER_INPUTS)) {
      // Derived, not parallel: no keyboard suppression without a live click
      // guard on the same composer.
      if (!GUARDED_WRITES.includes(sendSelector)) continue;
      const el = target.closest(inputSelector);
      if (!el) continue;
      event.stopPropagation();
      event.preventDefault();
      console.warn(
        `[web-install] write-guard: suppressed Enter on "${inputSelector}" — it calls "${sendSelector}"'s ` +
          `write directly, with no click for the click guard to see (Asana 1217799695855666).`,
      );
      onBlocked?.(sendSelector, el);
      return;
    }
  };

  win.document.addEventListener("click", onClick, true);
  win.document.addEventListener("keydown", onKeydown, true);
  return () => {
    win.document.removeEventListener("click", onClick, true);
    win.document.removeEventListener("keydown", onKeydown, true);
  };
}
